import { DestroyRef, Directive, effect, inject, input } from '@angular/core';
import { MatSort, type Sort as MaterialSort } from '@angular/material/sort';
import { Store } from '@ngxs/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SortAction } from '../state/action/sort.action';
import type { TableColumnConfig } from '../data/table-column-config';
import { assertIsNotNil } from '@pct-ts/assert';
import type { SortModel } from '../data/sort-model';
import type { PropertyKeys } from '@pct-ts/typing';
import type { Sort } from '../data/table-data-source';
import { isNil } from '@pct-ts/typeguard';
import { matOrderToOrderByDirection, orderByDirectionToMatOrder } from '../util/order-converter';
import { TableSortConfigException } from '../exception/table-sort-config-exception';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[matSort][szgCoreTableSort]',
})
export class TableSortDirective<T> {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #matSort: MatSort = inject(MatSort, { self: true });

  // eslint-disable-next-line  @angular-eslint/no-input-rename
  readonly columns = input.required<TableColumnConfig<T>[]>({ alias: 'szgCoreTableSort' });
  readonly actualSort = input<Sort<T>>();
  readonly tableId = input.required<string>();

  constructor() {
    this.#matSort.sortChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(sort => this.#emitSortEvent(sort));
    effect(() => this.#setupDefaultSort());
  }

  #emitSortEvent(sort: MaterialSort): void {
    const sortModel = this.#getSortModel(sort);
    if (this.#isMatchToActualSort(sortModel)) {
      return;
    }
    this.#store.dispatch(new SortAction(sortModel));
  }

  #setupDefaultSort(): void {
    const actualSort = this.actualSort();
    if (isNil(actualSort)) {
      return;
    }
    const columnId = this.#findSortedId(actualSort.field);
    const order = orderByDirectionToMatOrder(actualSort.order);
    this.#matSort.active = columnId;
    this.#matSort.direction = order;
  }

  #findSortedField(columnId: string): PropertyKeys<T> {
    const selectedColumn = this.columns().find(column => column.id === columnId);
    assertIsNotNil(
      selectedColumn,
      `Column with id '${columnId}' must exist in table configuration`,
    );
    return selectedColumn.field as PropertyKeys<T>;
  }

  #findSortedId(columnField: PropertyKeys<T>): string {
    const selectedColumn = this.columns().find(
      column => column.field === columnField && column.sortable,
    );
    if (isNil(selectedColumn)) {
      throw new TableSortConfigException(
        'Invalid sort config occurred! Actual sort points to no column or column that are not sortable.',
      );
    }
    return selectedColumn.id;
  }

  #isMatchToActualSort(sort: SortModel<T>): boolean {
    const actualSort = this.actualSort();
    if (isNil(actualSort)) {
      return false;
    }
    return sort.field === actualSort.field && sort.order === actualSort.order;
  }

  #getSortModel(sort: MaterialSort): SortModel<T> {
    if (sort.direction === '') {
      return { tableId: this.tableId() };
    }
    const field = this.#findSortedField(sort.active);
    return {
      tableId: this.tableId(),
      order: matOrderToOrderByDirection(sort.direction),
      field,
    };
  }
}
